import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@shared/shared.module';
import {
  CoverageFileResponse,
  CoverageRequirementMode,
  PeriodCoverageResponse,
  PeriodEntryResponse,
  TrackCoverageResponse,
  YearCoverageBlockResponse,
} from '@interfaces/accounting-document-management/responses/document-coverage.response';
import { ClassifiedDocumentUploadResponse } from '@interfaces/accounting-document-management/responses/classified-document-upload.response';
import {
  ClassifiedUploadProgress,
  SelectedCoveragePeriod,
} from '@features/dashboard/accounting-document-management/services/accounting-document-management-state.service';
import {
  CELL_STATE_CLASS,
  CELL_STATE_LABEL,
  CoverageCellState,
  monthLongLabel,
  monthShortLabel,
  resolveCellState,
} from '@features/dashboard/accounting-document-management/utils/coverage-view.util';

/** Um mês da barra da trilha, já com classe e título prontos para o template. */
interface StatementMonthCell {
  month: number;
  label: string;
  stateClass: string;
  title: string;
  /** `true` quando algum documento cobre o mês — é o que "preenche" a barra. */
  covered: boolean;
  /** `true` quando este mês é o período atualmente selecionado — mesmo contorno nas três trilhas. */
  selected: boolean;
}

/**
 * Indica se o período do inventário é o anual. O backend reporta o anual como `1–12` (a convenção
 * canônica de intervalo nulo é interna à regra dele), então é por aí que se reconhece.
 */
function isAnnualPeriod(period: PeriodEntryResponse): boolean {
  return period.startMonth === 1 && period.endMonth === 12;
}

/**
 * Tooltip de um período clicável, que também **anuncia** o que o clique faz: com arquivo, abre a
 * pré-visualização do considerado; sem arquivo, só seleciona.
 *
 * O caso "sem arquivo" não é uma falha e precisa ser dito com essas palavras: o acervo importado no
 * onboarding tem demonstrativo extraído (o período conta como coberto) e **nenhum** arquivo nesta tela.
 */
function periodTitle(label: string, files: readonly CoverageFileResponse[]): string {
  const considered = files.find(file => file.isConsidered);
  if (!considered) {
    // Rodada de correção 1 (Task 5B): "nenhum arquivo para visualizar" é falso quando existe uma
    // submissão aguardando aprovação — o documento chegou, só não tem extração (e por isso nunca é
    // `isConsidered`) para pré-visualizar. O clique já não tenta abrir nada nesse caso
    // (`onDocumentPeriodClick`, mais abaixo) — aqui só o texto precisava distinguir os dois "sem
    // considerado": nada enviado vs. enviado e aguardando decisão.
    const awaitingApproval = files.some(file => file.isPending);
    return awaitingApproval
      ? `${label} — aguardando aprovação`
      : `${label} — nenhum arquivo para visualizar`;
  }

  const replaced = files.length - 1;
  const suffix = replaced > 0 ? ` (+${replaced} substituído${replaced > 1 ? 's' : ''})` : '';
  return `${label} — clique para ver ${considered.fileName}${suffix}`;
}

/**
 * Uma trilha (DRE/Balanço/Faturamento) pronta para renderizar, com barra, chips e texto de
 * complemento. `requirementMode` decide qual conjunto de campos o template usa no cabeçalho: o botão
 * anual (regime `"Anual"`) ou a pílula de pendentes (regime `"Mensal"`).
 */
/**
 * Reinterpreta o estado de um mês do regime **mensal** quando existe arquivo ancorado nele que ainda não
 * virou cobertura. Devolve `null` quando não há nada a reinterpretar — mês coberto, mês sem arquivo ou mês
 * fora da janela obrigatória seguem exatamente como estavam.
 *
 * Existe porque "cobrir o período" no faturamento depende dos **valores extraídos**, e isso produzia uma
 * cegueira: entre o aceite do upload e a gravação dos valores — e para sempre, quando a extração concluía
 * sem encontrar nada — a célula ficava **idêntica** a "nada enviado". A pessoa subia o documento, via um
 * toast que desaparecia, e a régua não admitia que o arquivo existe.
 *
 * Os três casos que passam a se distinguir, todos com o arquivo já visível no painel do período:
 * - extração **na fila ou processando** → pinta como `analyzing`, o mesmo azul de "em análise": chegou e
 *   está andando, não é pendência de envio;
 * - extração **concluída sem valor nenhum** → mantém o tratamento de pendência, marcada para atenção;
 * - extração com **erro** → idem, com o motivo no título.
 *
 * Deliberadamente **não** inventa cor nova: reaproveita `analyzing` e a própria pendência, e a atenção é um
 * marcador por cima. A alternativa seria um sexto estado de célula numa régua que já tem cinco.
 */
function describeMonthlyArrival(
  month: PeriodCoverageResponse,
  state: CoverageCellState
): { state: CoverageCellState; title: string; needsAttention: boolean } | null {
  if (state === 'sent' || state === 'notRequired') return null;
  if (!month.files.length) return null;

  const emAndamento = month.files.filter(
    file => file.status === 'InQueue' || file.status === 'Processing'
  );
  if (emAndamento.length) {
    return {
      state: 'analyzing',
      title: `documento recebido, extração em andamento (${emAndamento.length} arquivo${emAndamento.length > 1 ? 's' : ''})`,
      needsAttention: false,
    };
  }

  return {
    state,
    title: month.files.some(file => file.status === 'Error')
      ? 'documento recebido, mas a extração falhou — precisa reenviar'
      : 'documento recebido, mas a extração não encontrou valores — precisa reenviar',
    needsAttention: true,
  };
}

interface TrackView {
  documentType: 'Dre' | 'Balanco' | 'Faturamento';
  label: string;
  requirementMode: CoverageRequirementMode;
  /** Só no regime anual: o botão do cabeçalho que é o controle da exigência. Nulo no regime mensal. */
  annual: PeriodCoverageResponse | null;
  annualSelected: boolean;
  /** `true` quando o exercício fechou e o anual ainda não chegou — o único estado de cobrança. */
  annualMissing: boolean;
  /**
   * `true` quando o anual já foi entregue (status fechado). Separado de `!annualMissing` de propósito:
   * um exercício ainda aberto (2026) não tem anual exigido e também não é "faltando", mas continua
   * **sem documento** — pintá-lo de entregue por exclusão diria que chegou algo que não chegou.
   */
  annualSent: boolean;
  /** Tooltip do botão anual, que também anuncia que o clique abre o arquivo quando há um. */
  annualTitle: string;
  /** Só no regime mensal: "3 pendentes" no cabeçalho, no lugar do botão anual. Nulo sem pendência. */
  pendingPill: string | null;
  months: StatementMonthCell[];
  /** Só os períodos **complementares** — o anual mora no botão do cabeçalho, não aqui. Sempre vazia no regime mensal. */
  periods: (PeriodEntryResponse & { selected: boolean; stateClass: string; title: string })[];
  /** Texto neutro do complemento. Nunca cobra mês no regime anual; no mensal nomeia a pendência real. */
  complementLabel: string;
}

/** Um exercício pronto para renderizar, nas duas visualizações. */
interface StatementYearView {
  year: number;
  badge: string;
  badgeClass: string;
  tracks: TrackView[];
  /** Frase "Cog AI leu X e classificou como Y", quando a última classificação foi deste exercício. */
  classificationSummary: string | null;
}

/**
 * Cobertura da aba "Demonstrações financeiras": três trilhas independentes (DRE, Balanço e
 * Faturamento) por exercício. DRE e Balanço têm barra de 12 meses complementar, chips dos períodos
 * enviados e área de envio por exercício com classificação automática; Faturamento tem barra de 12
 * meses **exigidos** mês a mês (sem botão anual, sem chips — ver {@link TrackCoverageResponse.requirementMode}).
 *
 * **Régua e Cartões são duas layouts do mesmo conteúdo**, e por isso vivem no mesmo componente: a
 * régua empilha os exercícios em linhas, os cartões distribuem em grade. Separá-los em dois
 * componentes duplicaria a construção das trilhas (barra + chips + selo), que é toda a lógica daqui.
 * A **Matriz** do protótipo foi descartada por decisão de produto.
 *
 * Apresentação pura: não injeta o state service, não chama HTTP e não recalcula cobertura — selo,
 * rótulo de período e granularidade vêm resolvidos do backend.
 */
@Component({
  selector: 'app-year-coverage',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './year-coverage.component.html',
  styleUrl: './year-coverage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YearCoverageComponent {
  readonly years = input.required<YearCoverageBlockResponse[]>();
  readonly viewMode = input<'regua' | 'cartoes'>('cartoes');
  readonly selectedPeriod = input<SelectedCoveragePeriod | null>(null);
  readonly lastClassification = input<{
    year: number | null;
    result: ClassifiedDocumentUploadResponse;
    /** Frase já traduzida pelo estado — o componente exibe, não remonta. */
    message: string;
  } | null>(null);
  /**
   * Envio classificado em andamento, vindo do estado da página. Carrega o **exercício** do envio, e é
   * isso que faz o estado "enviando" aparecer só na área daquele ano: o sinal do estado vale para a
   * tela inteira, e pintá-lo sem discriminar mentiria sobre onde o arquivo caiu (bug 2895).
   */
  readonly uploadProgress = input<ClassifiedUploadProgress | null>(null);

  /**
   * Gate de elegibilidade de envio (PBI #2728, 24/08/2026): `false` substitui a área de arraste de
   * **cada** exercício pelo aviso de onboarding — a cobertura (barra, chips, seleção) continua igual,
   * porque ela não depende de onboarding, só o envio depende. Default `true` para não inventar
   * bloqueio num consumidor futuro que esqueça de passar o input.
   */
  readonly canUpload = input(true);

  /**
   * Motivo do bloqueio pronto para exibição (`AccountingUploadEligibilityResponse.blockReason`), por
   * exemplo "O onboarding desta empresa ainda não foi iniciado.". `null` quando `canUpload()` é
   * `true` ou antes do primeiro carregamento do gate.
   */
  readonly uploadBlockReason = input<string | null>(null);

  /**
   * Template da inspeção inline — o painel de arquivos do período —, declarado uma vez pela página e
   * instanciado aqui dentro da trilha do período selecionado ({@link isSelectedTrack}). Até 21/08/2026
   * esse mesmo slot também trazia a área de envio travada por seleção; hoje selecionar é só consultar. `ng-content` não serve porque o
   * slot é por trilha **dentro de um `@for`** — daí o `TemplateRef` + `ngTemplateOutlet` em vez de
   * projeção de conteúdo comum.
   */
  readonly selectionTemplate = input<TemplateRef<unknown> | null>(null);

  readonly periodSelected = output<SelectedCoveragePeriod>();

  /**
   * Emitido, com o `extractionId`, quando o período clicado **já tem documento** — a página abre a
   * `FilePreviewModalComponent`. É sempre o arquivo *considerado* (o mais recente do período), que é o
   * que conta para a cobertura; os substituídos continuam acessíveis pela lista do painel.
   *
   * Vem junto com {@link periodSelected}, não em vez dele: o painel do período abre por baixo, então
   * fechar a pré-visualização deixa o backoffice já com a edição manual à mão — que é a ordem natural,
   * porque aprovar ou rejeitar exige ter visto o documento.
   */
  readonly filePreviewRequested = output<string>();

  /**
   * Arquivos escolhidos ou soltos na área de envio de um exercício — quem trata decide chamar a
   * classificação. É uma lista porque arrastar vários documentos de uma vez passou a enfileirar
   * todos, em vez de mandar o primeiro e descartar o resto calado (bug 2895).
   */
  readonly filesDroppedOnYear = output<{ year: number; files: readonly File[] }>();

  /**
   * Clique em "Ir para o onboarding" no aviso que substitui a área de envio quando `canUpload()` é
   * `false`. Sem payload de exercício — o destino é sempre o onboarding da empresa em escopo, não algo
   * específico do card em que o botão foi clicado. Sem estado nem HTTP nesta apresentação pura: quem
   * navega é a página, que sabe a empresa selecionada.
   */
  readonly onboardingRequested = output<void>();

  protected readonly isRuler = computed(() => this.viewMode() === 'regua');

  /**
   * Exercício sob o arraste, para destacar **só** a área que receberia o arquivo. `null` = nenhum.
   * Apresentação pura: é estado de ponteiro, não sai do componente.
   */
  protected readonly dragOverYear = signal<number | null>(null);

  /**
   * `true` enquanto **qualquer** envio está em andamento. Durante ele nenhuma área aceita arquivo novo
   * (nem por clique, nem por arraste): o envio é um por vez e permitir um segundo em outro exercício
   * daria dois POSTs concorrentes com um único estado de progresso.
   */
  protected readonly isUploading = computed(() => this.uploadProgress() !== null);

  protected readonly yearViews = computed<StatementYearView[]>(() =>
    this.years().map(year => this.buildYearView(year))
  );

  /**
   * `true` só para o exercício que está de fato enviando — é ele que troca a área pelo estado de
   * andamento. Os outros exercícios seguem com a área normal (bloqueada, mas sem dizer "enviando").
   */
  protected isUploadingYear(year: number): boolean {
    return this.uploadProgress()?.year === year;
  }

  /** A trilha (exercício + tipo de documento) é a dona do período selecionado — é onde o slot de envio nasce. */
  protected isSelectedTrack(
    year: number,
    documentType: 'Dre' | 'Balanco' | 'Faturamento'
  ): boolean {
    const selected = this.selectedPeriod();
    return selected !== null && selected.year === year && selected.documentType === documentType;
  }

  protected onPeriodClick(
    year: number,
    documentType: 'Dre' | 'Balanco' | 'Faturamento',
    startMonth: number,
    endMonth: number
  ): void {
    this.periodSelected.emit({ year, documentType, startMonth, endMonth });
  }

  /**
   * Clique num período que representa um documento — o slot anual ou um chip. Seleciona o período e, se
   * houver arquivo, pede a pré-visualização dele.
   */
  protected onDocumentPeriodClick(
    year: number,
    documentType: 'Dre' | 'Balanco' | 'Faturamento',
    startMonth: number,
    endMonth: number,
    files: readonly CoverageFileResponse[]
  ): void {
    this.onPeriodClick(year, documentType, startMonth, endMonth);

    // Período sem arquivo não abre nada: é o caso do acervo importado no onboarding, que tem
    // demonstrativo extraído e nenhum arquivo nesta tela. Abrir uma modal vazia (ou de erro) seria pior
    // do que só selecionar e deixar o painel dizer que não há arquivo.
    const considered = files.find(file => file.isConsidered);
    if (considered) this.filePreviewRequested.emit(considered.extractionId);
  }

  protected onFilesSelected(event: Event, year: number): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length) this.filesDroppedOnYear.emit({ year, files });
    input.value = '';
  }

  /**
   * `dragenter` **precisa** ser cancelado, e não só `dragover`: era exatamente isso que faltava para o
   * arraste funcionar (bug 2895). Medido no navegador com arraste real — o `onDragOver` daqui rodava e
   * cancelava certo (232 vezes, `dropEffect=copy`), e o `drop` **nunca** era entregue; bastou cancelar
   * `dragenter` no mesmo contêiner para o evento chegar ao handler que já existia. Sem o cancelamento,
   * o alvo aceito pelo `dragover` é invalidado toda vez que o ponteiro cruza a fronteira de um `<span>`
   * filho, e o drop cai no vazio — o que explica o "arrastei e o arquivo voltou".
   */
  protected onDragEnter(event: DragEvent, year: number): void {
    if (this.isUploading()) return this.refuseDrag(event);
    event.preventDefault();
    this.dragOverYear.set(year);
  }

  protected onDragOver(event: DragEvent, year: number): void {
    if (this.isUploading()) return this.refuseDrag(event);
    event.preventDefault();
    this.dragOverYear.set(year);
  }

  /**
   * Recusa o arraste **cancelando o evento**, em vez de ignorá-lo. Sair sem `preventDefault()` devolve
   * o drop ao navegador, que abre o arquivo solto e descarrega a SPA — abortando justamente o envio em
   * andamento que motivou a recusa. O `dropEffect = 'none'` troca o cursor para "não é possível soltar
   * aqui", que é o retorno honesto enquanto a área está ocupada.
   */
  protected refuseDrag(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
  }

  /**
   * Só desliga o destaque quando o ponteiro sai **do contêiner**. `dragleave` também dispara ao passar
   * de um `<span>` filho para outro dentro da própria área (num único arraste foram medidos 16 pares
   * dragenter/dragleave), e apagar o realce nesses casos o faria piscar.
   */
  protected onDragLeave(event: DragEvent): void {
    const zone = event.currentTarget as HTMLElement;
    const entering = event.relatedTarget as Node | null;
    if (entering && zone.contains(entering)) return;
    this.dragOverYear.set(null);
  }

  protected onDrop(event: DragEvent, year: number): void {
    event.preventDefault();
    this.dragOverYear.set(null);
    if (this.isUploading()) return;

    // Arrastar o que não é arquivo (texto selecionado, imagem de página, pasta) chega aqui com a lista
    // vazia: sai sem emitir nada e sem deixar a área presa no destaque, já desligado acima.
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length) this.filesDroppedOnYear.emit({ year, files });
  }

  private buildYearView(year: YearCoverageBlockResponse): StatementYearView {
    const classification = this.lastClassification();

    return {
      year: year.year,
      badge: year.badge,
      badgeClass: `statement-badge statement-badge--${year.badgeVariant}`,
      tracks: year.tracks.map(track => this.buildTrackView(year.year, track)),
      // Exibe a MESMA frase do toast (montada uma vez no estado, por `buildClassificationMessage`): este
      // chip fica visível depois que o toast some, e remontar o texto aqui abriria espaço para os dois
      // divergirem — inclusive no sufixo "Aguardando aprovação" (Task 5 do PBI 2728), que precisa
      // acompanhar o chip enquanto a submissão não for aprovada.
      classificationSummary:
        classification &&
        classification.year === year.year &&
        !classification.result.needsHumanReview
          ? classification.message
          : null,
    };
  }

  private buildTrackView(year: number, track: TrackCoverageResponse): TrackView {
    const selected = this.selectedPeriod();
    const isAnnualMode = track.requirementMode === 'Anual';
    const months = this.buildMonths(track, year);

    return {
      documentType: track.documentType,
      label: track.label,
      requirementMode: track.requirementMode,
      annual: track.annual,
      annualSelected:
        isAnnualMode && track.annual
          ? this.isSelected(selected, year, track.documentType, 1, 12)
          : false,
      annualMissing:
        isAnnualMode && track.annual ? track.annual.isRequired && !track.hasAnnual : false,
      annualSent: isAnnualMode && !!track.annual && track.hasAnnual,
      annualTitle: isAnnualMode && track.annual ? periodTitle('Anual', track.annual.files) : '',
      pendingPill: isAnnualMode ? null : this.buildPendingPill(track),
      months,
      // O anual sai dos chips: ele já tem o botão dedicado no cabeçalho da trilha, que é o controle da
      // exigência (e o único que sinaliza a falta, pelo contorno pontilhado). Mantê-lo aqui rendia dois botões
      // "Anual" na mesma trilha, com o mesmo rótulo e disparando exatamente o mesmo período — o que a
      // própria legenda já contradizia, listando como complementares só "semestre, trimestre, mês e
      // acumulado". Os chips são o complemento; o anual é a exigência. No regime mensal (Faturamento)
      // `track.periods` já vem vazia do backend, então o filtro abaixo é inofensivo.
      //
      // O filtro é de APRESENTAÇÃO, não de contrato: `track.periods` continua sendo o inventário completo
      // e o backend depende disso para contar rejeitados e decidir "nada enviado".
      periods: track.periods
        .filter(period => !isAnnualPeriod(period))
        .map(period => ({
          ...period,
          selected: this.isSelected(
            selected,
            year,
            track.documentType,
            period.startMonth,
            period.endMonth
          ),
          stateClass: `statement-chip statement-chip--${period.status.toLowerCase()}`,
          title: periodTitle(period.longLabel, period.files),
        })),
      complementLabel: this.buildComplementLabel(track, months),
    };
  }

  /** Texto da pílula de pendentes do cabeçalho, no regime mensal. Nulo quando não falta nenhum mês. */
  private buildPendingPill(track: TrackCoverageResponse): string | null {
    const pending = track.requiredCount - track.coveredCount;
    return pending > 0 ? `${pending} pendente${pending > 1 ? 's' : ''}` : null;
  }

  /**
   * Monta as 12 células da barra da trilha, uma por mês, dividindo pelo regime de exigência
   * ({@link TrackCoverageResponse.requirementMode}) — a chave para saber se o mês carrega o
   * `isRequired` real (Faturamento) ou é só complemento (DRE/Balanço).
   */
  private buildMonths(track: TrackCoverageResponse, year: number): StatementMonthCell[] {
    const selected = this.selectedPeriod();

    if (track.requirementMode === 'Mensal') {
      // Regime mensal: cada mês já carrega o `isRequired` real da janela obrigatória, e
      // `resolveCellState` é a mesma função que pinta a grade — mês exigido e vazio vira `pending`
      // (tracejado), mês fora da janela (corrente/futuro) vira `notRequired` (cinza chapado).
      return track.months.map(month => {
        const state = resolveCellState(month);
        const arrival = describeMonthlyArrival(month, state);
        return {
          month: month.month,
          label: monthShortLabel(month.month),
          stateClass:
            `statement-month statement-month--${CELL_STATE_CLASS[arrival?.state ?? state]}` +
            (arrival?.needsAttention ? ' statement-month--attention' : ''),
          title: arrival
            ? `${monthLongLabel(month.month)} — ${arrival.title}`
            : `${monthLongLabel(month.month)} — ${CELL_STATE_LABEL[state]}`,
          covered: state === 'sent',
          selected: this.isSelected(selected, year, track.documentType, month.month, month.month),
        };
      });
    }

    // Regime anual: regra de união preservada (ver decisão #1 do README da feature) — não alterar sem
    // decisão de produto nova. "Coberto" é a UNIÃO de `!uncoveredMonths.has(mes)` (demonstrativo no
    // inventário, inclusive o acervo importado no onboarding, sem nenhum arquivo aqui) com
    // `files.length > 0` (arquivo que chegou e ainda não foi extraído).
    const uncovered = new Set(track.uncoveredMonths);
    return track.months.map(month =>
      this.buildAnnualModeMonthCell(month, track, uncovered, year, selected)
    );
  }

  /**
   * Monta a célula de um mês do regime **anual** (DRE/Balanço). Mês sem documento usa deliberadamente
   * o tratamento visual de **não exigido**, nunca o de pendência: nas demonstrações a exigência é só o
   * anual, e uma barra que "cobra" mês contradiria a regra de negócio (ver README da feature).
   *
   * "Coberto" é a **união** de duas coisas, e precisa das duas:
   * 1. o mês **não** estar em `uncoveredMonths` — é o backend dizendo que existe demonstrativo
   *    extraído cobrindo o mês. É a fonte de verdade da cobertura, e vale mesmo sem nenhum arquivo na
   *    Central de Documentos (acervo importado no onboarding é assim: tem DRE e Balanço, nenhum
   *    arquivo aqui);
   * 2. haver arquivo no período — documento que chegou mas cuja extração ainda não rodou, então ainda
   *    não está no inventário. Pintar como vazio esconderia um upload que o usuário acabou de fazer.
   *
   * Usar só `files.length`, como esta régua fazia, apagava a barra de todo exercício vindo do
   * onboarding — a barra ficava cinza enquanto o texto ao lado dizia "todos os meses com documento".
   */
  private buildAnnualModeMonthCell(
    month: PeriodCoverageResponse,
    track: TrackCoverageResponse,
    uncoveredMonths: Set<number>,
    year: number,
    selected: SelectedCoveragePeriod | null
  ): StatementMonthCell {
    const covered = !uncoveredMonths.has(month.month) || month.files.length > 0;
    const state = covered ? this.resolveCoveredMonthState(month) : 'notRequired';

    return {
      month: month.month,
      label: monthShortLabel(month.month),
      stateClass: `statement-month statement-month--${CELL_STATE_CLASS[state]}`,
      title: covered
        ? `${monthShortLabel(month.month)} — ${CELL_STATE_LABEL[state]} (${track.label})`
        : `${monthShortLabel(month.month)} — sem documento (não exigido)`,
      covered,
      selected: this.isSelected(selected, year, track.documentType, month.month, month.month),
    };
  }

  /**
   * Estado visual de um mês que **tem** documento, no regime anual.
   *
   * Não dá para usar `resolveCellState` cru aqui: quando o status de conformidade é `Pending` ele cai
   * em `isRequired` para decidir, e como mês complementar nunca é exigido o resultado seria
   * `notRequired` — a barra ficaria apagada, como se nada tivesse chegado. E esse é exatamente o
   * estado normal na janela entre o upload e o dispatcher da fila marcar `Received`, que é assíncrono
   * neste fluxo. Documento na mão e barra vazia seria mentira.
   *
   * Então: `Received`/`Approved`/`Analyzing`/`Rejected` seguem `resolveCellState`; `Pending` com
   * arquivo vira `analyzing` ("chegou, aguardando validação").
   */
  private resolveCoveredMonthState(month: PeriodCoverageResponse) {
    const state = resolveCellState(month);
    return state === 'notRequired' || state === 'pending' ? 'analyzing' : state;
  }

  /**
   * Texto neutro do complemento da trilha, dividido pelo regime — só o mensal nomeia pendência. No
   * regime anual reaproveita `cells` (já construídas por {@link buildMonths}) em vez de recalcular a
   * regra de união — mesma fonte, uma conta só.
   */
  private buildComplementLabel(track: TrackCoverageResponse, cells: StatementMonthCell[]): string {
    if (track.requirementMode === 'Mensal') {
      const pending = track.requiredCount - track.coveredCount;
      const enviados = `${track.coveredCount} de ${track.requiredCount} meses enviados`;
      return pending > 0 ? `${enviados} · ${pending} pendente${pending > 1 ? 's' : ''}` : enviados;
    }

    const coveredCount = cells.filter(cell => cell.covered).length;
    return coveredCount === 12
      ? 'Todos os meses com documento'
      : `${coveredCount} de 12 meses enviados`;
  }

  private isSelected(
    selected: SelectedCoveragePeriod | null,
    year: number,
    documentType: 'Dre' | 'Balanco' | 'Faturamento',
    startMonth: number,
    endMonth: number
  ): boolean {
    return (
      selected !== null &&
      selected.year === year &&
      selected.documentType === documentType &&
      selected.startMonth === startMonth &&
      selected.endMonth === endMonth
    );
  }
}
